import type { ResolvedCluster } from '~/stores/clusterRegistry';
import type { ResolvedCurrentCluster } from '~/utils/clusters';

import { useClusterRegistryStore } from '~/stores/clusterRegistry';
import { buildApiUrl, EMPTY_CLUSTER } from '~/utils/clusters';
import { isServer } from '~/utils/runtime';

export interface CurrentClusterContext {
  /** Cluster code (e.g. `au-cluster`). Synchronous; comes from shared state. */
  code: ComputedRef<string | null>;
  /** Cluster path prefix (e.g. `/au`, or empty for the root cluster). */
  pathPrefix: ComputedRef<string>;
  /** Resolved cluster from the registry. Null until the registry is loaded. */
  cluster: ComputedRef<ResolvedCluster | null>;
  /** Scheme + host only, never the path prefix — a valid RFC 6454 Origin. */
  origin: ComputedRef<string>;
  baseUrl: ComputedRef<string>;
  apiUrl: ComputedRef<string>;
  host: ComputedRef<string>;
}

/**
 * Resolve the current cluster from the shared Nuxt state populated by
 * `~/router.options.ts` from the `X-Cluster-Code` header on SSR. Client
 * picks it up from the hydrated payload — no env / window.location parsing.
 */
export const useCurrentCluster = (): CurrentClusterContext => {
  const config = useRuntimeConfig();
  const host = useRequestHost();
  const registry = useClusterRegistryStore();

  if (!host) {
    throw new Error('Host is undefined');
  }

  const siteHost = host.replace(/^www\./, '').replace(/:\d+$/, '');

  const useHttpForApi = isServer && config.public.useHttpForApiOnServer;
  const useHttp = useHttpForApi || config.public.useHttp;

  const clusterState = useState<ResolvedCurrentCluster>('cluster', () => EMPTY_CLUSTER);

  const code = computed(() => clusterState.value.code);

  const cluster = computed<ResolvedCluster | null>(() =>
    code.value ? (registry.findByCode(code.value) ?? null) : null,
  );

  // Both sides are the same value (the cluster's pathPrefix) from two sources:
  // clusterState.pathPrefix is the env-derived SSR bootstrap (router.options.ts);
  // once the registry hydrates, its DB-backed pathPrefix is authoritative, so
  // baseUrl/apiUrl and the hreflang/canonical URLs (plugins/03.seo.ts) resolve
  // against one source. The two must agree per cluster (env === DB path_prefix).
  const pathPrefix = computed(() => cluster.value?.pathPrefix ?? clusterState.value.pathPrefix);

  // Origin omits the path prefix: Sanctum's stateful check matches on
  // scheme+host only, so a path-bearing value (e.g. `…/au`) never matches.
  const origin = computed(() => {
    const scheme = useHttp ? 'http' : 'https';
    return `${scheme}://${siteHost}`;
  });

  const baseUrl = computed(() => `${origin.value}${pathPrefix.value}`);

  const apiUrl = computed(() =>
    buildApiUrl(host, pathPrefix.value, { useHttp, withPort: isServer }),
  );

  return {
    code,
    pathPrefix,
    cluster,
    origin,
    baseUrl,
    apiUrl,
    host: computed(() => siteHost),
  };
};
